import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, status: "pending" });

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ loading: false, status: "paid", data });
          return;
        }
      } catch (e) {
        // Payment not yet visible — expected during polling. Log for debugging only.
        // eslint-disable-next-line no-console
        console.debug("Payment poll attempt failed (retrying):", e?.response?.status || e?.message);
      }
      if (tries++ < 10) setTimeout(poll, 2000);
      else setState({ loading: false, status: "timeout" });
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-linen grid place-items-center p-8">
      <div className="max-w-md w-full card-widget text-center py-12">
        {state.loading ? (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-ink animate-spin" />
            <h1 className="mt-6 font-display text-2xl">Confirming payment…</h1>
            <p className="mt-2 text-sm text-ink-mid">Please wait while we verify your transaction.</p>
          </>
        ) : state.status === "paid" ? (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-[#2D6A4F]" />
            <h1 className="mt-6 font-display text-3xl">Payment successful 🎉</h1>
            <p className="mt-2 text-sm text-ink-mid">Thank you — your payment has been recorded.</p>
            <Link to="/app" data-testid="back-to-app" className="mt-8 inline-block btn-primary">Back to dashboard</Link>
          </>
        ) : (
          <>
            <XCircle className="w-16 h-16 mx-auto text-[#D06A42]" />
            <h1 className="mt-6 font-display text-2xl">We couldn't confirm payment yet</h1>
            <p className="mt-2 text-sm text-ink-mid">If you completed the checkout, it may take a few more moments to sync. Refresh this page or check your invoice status shortly.</p>
            <Link to="/app" className="mt-8 inline-block btn-primary">Back to dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-linen grid place-items-center p-8">
      <div className="max-w-md w-full card-widget text-center py-12">
        <XCircle className="w-16 h-16 mx-auto text-ink-mid" />
        <h1 className="mt-6 font-display text-3xl">Payment cancelled</h1>
        <p className="mt-2 text-sm text-ink-mid">No charges were made. You can try again anytime.</p>
        <Link to="/app" className="mt-8 inline-block btn-primary">Back to dashboard</Link>
      </div>
    </div>
  );
}
